/**
 * Data model of the DMS product line tree: product lines at level 0, their
 * models at level 1. Kept apart from the component so the flattening is
 * testable.
 */
import { DMS_PRODUCTLINES } from '@/shared/constants';

/** Node given by the screens: `data` is a product line at level 0, an item at level 1. */
export interface DmsTreeNode<Data = unknown> {
  name: string;
  data: Data;
  children?: DmsTreeNode<Data>[];
}

/** Flattened node, as emitted on click. */
export interface DmsFlatNode<Data = unknown> {
  expandable: boolean;
  name: string;
  data: Data;
  level: number;
}

/** A level 0 node's data: one of the seven product lines. */
export interface DmsProductLineEntry {
  label: string;
  value: string;
}

export const isDmsProductLineEntry = (
  data: unknown,
): data is DmsProductLineEntry =>
  typeof data === 'object' &&
  data !== null &&
  'value' in data &&
  typeof data.value === 'string' &&
  'label' in data &&
  typeof data.label === 'string';

export type DmsProductLineNode = DmsTreeNode<DmsProductLineEntry>;

/** The seven product lines with no children: the tree before the items are loaded. */
export const initialDmsTreeData = (): DmsProductLineNode[] =>
  DMS_PRODUCTLINES.map((line) => ({
    name: line.label,
    data: line,
    children: [],
  }));

/** Depth-first flattening, children included only under expanded branches (Material flat tree). */
export const flattenDmsTree = <Data>(
  nodes: readonly DmsTreeNode<Data>[],
  isExpanded: (node: DmsTreeNode<Data>, level: number) => boolean,
  level = 0,
): DmsFlatNode<Data>[] =>
  nodes.flatMap((node) => {
    const expandable = !!node.children && node.children.length > 0;
    const flat: DmsFlatNode<Data> = {
      expandable,
      name: node.name,
      data: node.data,
      level,
    };
    return expandable && isExpanded(node, level)
      ? [flat, ...flattenDmsTree(node.children!, isExpanded, level + 1)]
      : [flat];
  });

/** A node is underlined when it is the selected product line or the selected model. */
export const isDmsNodeSelected = (
  node: { name: string; data: unknown },
  selectedParent: string,
  selectedNode: string,
): boolean =>
  (isDmsProductLineEntry(node.data) && node.data.value === selectedParent) ||
  node.name === selectedNode;

/**
 * Every product line as a
 * branch, with the items of that line as leaves (items of an unknown line are
 * dropped).
 */
export const buildDmsTree = <
  Item extends { data: { productLine?: string | null | undefined } },
>(
  items: readonly Item[],
  nameOf: (item: Item) => string,
): DmsTreeNode<DmsProductLineEntry | Item>[] =>
  DMS_PRODUCTLINES.map((line) => ({
    name: line.label,
    data: line,
    children: items
      .filter((item) => item.data.productLine === line.value)
      .map((item) => ({ name: nameOf(item), data: item, children: [] })),
  }));
